import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Space, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import {
  STATUS_OPEN,
  statusOptions,
} from '@totem/components/administration/deviceSyncMerges/containerConfiguration';
import DeviceSyncDismissModal from '@totem/components/administration/deviceSyncMerges/DeviceSyncDismissModal';
import DeviceSyncMergeModal from '@totem/components/administration/deviceSyncMerges/DeviceSyncMergeModal';
import DeviceSyncMergesContext from '@totem/components/administration/deviceSyncMerges/DeviceSyncMergesContext';
import DeviceSyncSource, {
  DeviceKeyList,
} from '@totem/components/administration/deviceSyncMerges/DeviceSyncSource';
import { SyncConflict } from '@totem/components/administration/deviceSyncMerges/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { DateStringToLocal } from '@totem/utilities/dateUtilities';

const styles = {
  device: { marginBottom: '0.5rem' },
  deletedTag: { marginLeft: 8 },
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'OPEN':
      return 'warning';
    case 'MERGED':
      return 'success';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string =>
  statusOptions.find((opt) => opt.value === status)?.text ?? status;

const DeviceSyncMergesTable = () => {
  const { conflicts, loading, totalRecords, refetch } = useContext(
    DeviceSyncMergesContext,
  );
  const { handleTableChange, input, getFilterOptions } =
    useContext(InputContext);
  const [mergeConflict, setMergeConflict] = useState<SyncConflict | null>(null);
  const [dismissConflict, setDismissConflict] = useState<SyncConflict | null>(
    null,
  );

  const columns: ColumnProps<SyncConflict>[] = [
    {
      title: 'Tool Record',
      key: 'source',
      render: (_, record: SyncConflict) => (
        <DeviceSyncSource conflict={record} />
      ),
    },
    {
      title: 'Matched Portal Devices',
      key: 'device',
      sorter: true,
      render: (_, record: SyncConflict) => (
        <span>
          {(record.devices ?? []).map((dev) => (
            <div key={dev.id} style={styles.device}>
              <Link to={`/dashboard/devices/${dev.id}`}>
                {dev.displayName || dev.id}
              </Link>
              {dev.isDeleted && <Tag style={styles.deletedTag}>Deleted</Tag>}
              <DeviceKeyList keys={dev.matchedKeys} />
            </div>
          ))}
        </span>
      ),
    },
    {
      title: 'Organization',
      key: 'organization',
      sorter: true,
      filters: getFilterOptions('organization'),
      filterMultiple: true,
      render: (_, record: SyncConflict) =>
        record.isCrossOrganization ? (
          <Tag color="error">Multiple organizations</Tag>
        ) : (
          <span>{record.organizationName}</span>
        ),
    },
    {
      title: 'Occurrences',
      key: 'occurrences',
      sorter: true,
      dataIndex: 'occurrences',
      width: '120px',
    },
    {
      title: 'First Seen',
      key: 'firstSeen',
      sorter: true,
      render: (_, record: SyncConflict) => (
        <span>{DateStringToLocal(record.firstSeen)}</span>
      ),
      width: '180px',
    },
    {
      title: 'Last Seen',
      key: 'lastSeen',
      sorter: true,
      render: (_, record: SyncConflict) => (
        <span>{DateStringToLocal(record.lastSeen)}</span>
      ),
      width: '180px',
    },
    {
      title: 'Status',
      key: 'status',
      sorter: true,
      filters: getFilterOptions('status'),
      filterMultiple: true,
      render: (_, record: SyncConflict) => (
        <Tag color={getStatusColor(record.status)}>
          {getStatusLabel(record.status)}
        </Tag>
      ),
      width: '120px',
    },
    {
      title: '',
      key: 'actions',
      render: (_, record: SyncConflict) =>
        record.status === STATUS_OPEN ? (
          <Space>
            {!record.isCrossOrganization && (
              <Button size="small" onClick={() => setMergeConflict(record)}>
                Merge
              </Button>
            )}
            <Button size="small" onClick={() => setDismissConflict(record)}>
              Dismiss
            </Button>
          </Space>
        ) : (
          <span>{record.resolvedBy}</span>
        ),
      width: '170px',
    },
  ];

  const closeModal = (updated: boolean) => {
    setMergeConflict(null);

    setDismissConflict(null);
    if (updated) {
      refetch();
    }
  };

  return (
    <>
      <IBTable
        columns={columns}
        dataSource={conflicts}
        loading={loading}
        rowKey={(record) => record.id}
        onChange={handleTableChange}
        pagination={{
          current: input.pagination.page,
          pageSize: input.pagination.pageSize,
          total: totalRecords,
          showSizeChanger: true,
        }}
      />
      {mergeConflict !== null && (
        <DeviceSyncMergeModal conflict={mergeConflict} onClose={closeModal} />
      )}
      {dismissConflict !== null && (
        <DeviceSyncDismissModal
          conflict={dismissConflict}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default DeviceSyncMergesTable;
